<template>
  <div class="provider-branded-home">

    <div id="stickyBlockStartPoint" />

    <div class="section-container theme-gallery-gradient-background">
      <!-- ========== Hero Section ========== -->
      <div class="section container px-4">
        <div class="branded-cover-image-container">
          <template v-if="!loaded">
            <SkeletonUI type="img" class="cover-image" />
          </template>
        </div>
        <div class="gallery-hero-section-container">
          <div class="gallery-hero-section row space-top-2">
            <div class="gallery-hero-description-section col-md-6 space-left-md-1 space-right-md-1 space-left-lg-1 space-right-lg-1 space-left-xl-1 space-right-xl-1 col-12">
              <h1 class="title font-lg-size-5">{{ providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hero_section && providerInfo[0].theme_data.landing.hero_section.title ? providerInfo[0].theme_data.landing.hero_section.title : 'Grow Your Business with Our Open API Solutions' }}</h1>
            </div>
            <div :class="`gallery-hero-description-section col-md-6 col-12`">
              <p class="description">{{ providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hero_section && providerInfo[0].theme_data.landing.hero_section.description ? providerInfo[0].theme_data.landing.hero_section.description : 'Discover our flexible API plans for different stages of your business' }}</p>
              <div v-if="providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hero_section && providerInfo[0].theme_data.landing.hero_section.buttons && providerInfo[0].theme_data.landing.hero_section.buttons.length > 0" class="button-container flex-column flex-lg-row">
                <a
                  v-for="(button, index) in providerInfo[0].theme_data.landing.hero_section.buttons"
                  :key="button.text"
                  :class="`${index % 2 === 0 ? 'theme-button btn btn-primary mr-2 mb-2 mr-lg-2 text-white' : 'theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline'} theme-gallery-button`"
                  :href="button.url"
                  target="_blank"
                >{{ button.text }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- ========== End Hero Section ========== -->

      <!-- ========== Image Carousel ========== -->
      <div
        v-if="providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.ts_section && providerInfo[0].theme_data.landing.ts_section.image_url && providerInfo[0].theme_data.landing.ts_section.image_url.length > 0"
        class="space-top-2 pb-2"
      >
        <Slick ref="slick" class="api-carousel" :options="slickOptions">
          <div
            v-for="(image, imgIndex) in providerInfo[0].theme_data.landing.ts_section.image_url"
            :key="imgIndex"
            class="js-slide bg-img-hero gallery-carousel-images-container"
          >
            <img
              v-img="{ type:'xl', class: 'gallery-cover-image' }"
              class="w-100 h-100 default-img gallery-cover-image"
              :src="image"
            >
          </div>
        </Slick>
      </div>
      <!-- ========== End Image Carousel ========== -->

      <!-- ========== Quick Link Section ========== -->
      <div class="section space-1 space-md-2">
        <div class="container px-4">
          <div class="row justify-content-center">
            <div v-for="(card, index) in home_cards" :key="index" class="col-md-4 mb-5 mb-md-0 mb-lg-0 mb-xl-0 boxed-container">
              <!-- Intro Card -->
              <div class="card h-100 transition-3d-hover gallery-card">
                <div class="card-body gallery-card-body pr-lg-4 pt-lg-2 pl-lg-4 pb-lg-2">
                  <div class="gallery-card-icon text-white">
                    <i v-if="card.id === 'how-it-works'" class="far fa-briefcase" />
                    <i v-else-if="card.id === 'getting-started'" class="far fa-lightbulb" />
                    <i v-else class="far fa-code" />
                  </div>
                  <div class="media align-items-center mb-3">
                    <div class="media-body">
                      <h4 class="card-title mb-0 text-white">{{ $t(card.title) }}</h4>
                    </div>
                  </div>
                  <p class="quick-link-description text-white">{{ $t(card.description, { provider: card.provider_display_name ? providerInfo[0].display_name : undefined }) }}</p>
                </div>
                <div class="gallery-card-footer font-weight-bold">
                  <router-link
                    class="theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline theme-gallery-button"
                    :to="{ name: 'provider-info-branded', params: { provider_name: $route.params.provider_name, tab_name: card.link }, hash: card.hash }"
                  >
                    {{ $t(card.link_text) }}
                  </router-link>
                </div>
              </div>
              <!-- End Intro Card -->
            </div>
          </div>
        </div>
      </div>
      <!-- ========== End Quick Link Section ========== -->

      <!-- ========== Trending APIs ========== -->
      <div v-if="latestApis.length > 0" class="grouped-section space-1 space-md-2 mt-3 mt-md-9 mb-3 mb-md-9 gallery-card pr-2 pl-2">
        <div class="container">
          <div class="w-100 mb-2 d-flex justify-content-between align-items-center">
            <!-- Title -->
            <h2 class="text-white">{{ $t('M05A-1-09') }}</h2>
            <router-link
              class="theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline theme-gallery-button"
              :to="{ name: 'provider-info-branded', params: { provider_name: $route.params.provider_name, tab_name: 'api' } }"
            >{{ $t('M05A-1-10a') }}</router-link>
          </div>
        </div>
        <div class="container position-relative">
          <hr>
          <div class="gallery-line-background" />
        </div>
        <div class="container">
          <div class="space-1 ml-1 mr-1">
            <ApiCarousel
              :key="`latest-apis-${Date.now()}`"
              class="boxed-api-carousel-featured"
              :items="latestApis"
              :apiCardClickHandler="goToApiInfo"
              :isBranded="true"
              :fromProvider="true"
              carouselId="api-carousel-latest"
              :loading="loading"
            />
          </div>
        </div>
        <!-- <div class="d-flex justify-content-center align-items-center mb-2"> -->
        <!-- TO-DO: i18n for button name -->
        <!-- <router-link
            class="theme-button btn btn-primary mr-0 mt-4 mb-lg-0 text-white boxed-small-btn"
            :to="{ name: 'provider-info-branded', params: { provider_name: $route.params.provider_name, tab_name: 'api' }, hash: '' }"
            target="_blank"
          >
            View All APIs
          </router-link>
        </div> -->
      </div>
      <!-- ========== End Trending APIs ========== -->

      <!-- ========== Feature Section ========== -->
      <div v-if="providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing" class="grouped-section space-1 space-md-2">
        <div v-if="providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.feature_section && providerInfo[0].theme_data.landing.feature_section.title">
          <div class="container project-highlights-section">
            <div class="d-flex justify-content-start mb-5 mb-lg-0 w-100">
              <h2 class="col-12 text-white text-left">
                {{ providerInfo[0].theme_data.landing.feature_section.title }}
              </h2>
            </div>
            <div class="container position-relative">
              <hr>
              <div class="gallery-line-background" />
            </div>
            <div class="row justify-content-md-center align-items-md-center">
              <div v-if="providerInfo[0].theme_data.landing.feature_section.statistics && providerInfo[0].theme_data.landing.feature_section.statistics.length > 0" class="d-flex flex-column flex-md-row w-100 gallery-card space-2 mt-4 mx-3">
                <div v-for="(statistic, index) in providerInfo[0].theme_data.landing.feature_section.statistics" :key="index" class="statistic-container mb-3 mb-md-0 d-flex justify-content-center col-md-4">
                  <div class="statistic">
                    <p class="number text-white mb-0">{{ statistic.prefix }}{{ statistic.number }}{{ statistic.suffix }}</p>
                    <h3 class="subtitle text-white">{{ statistic.subtitle }}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.feature_section && providerInfo[0].theme_data.landing.feature_section.features && providerInfo[0].theme_data.landing.feature_section.features.length > 0"
          :class="`${providerInfo[0].theme_data.landing.feature_section.statistics && providerInfo[0].theme_data.landing.feature_section.statistics.length > 0 ? 'space-1 space-lg-2' : 'space-bottom-1 space-bottom-lg-2 mt-4'}`"
        >
          <div class="container d-flex flex-wrap px-0">
            <div v-for="feature in providerInfo[0].theme_data.landing.feature_section.features" :key="feature.title" class="col-12 col-md-6 col-lg-6 mb-5 boxed-container">
              <!-- Feature Card -->
              <div class="card h-100 transition-3d-hover gallery-card">
                <div class="card-body gallery-card-body pr-3 pt-5 pl-3 pb-4 d-lg-flex">
                  <div class="mr-2 col-12 col-lg-2 mb-3 mb-lg-0">
                    <div class="boxed-circle-card-icon theme-circle-container-background theme-gallery-circle-container-background">
                      <i :class="feature.icon" />
                    </div>
                  </div>
                  <div class="col-12 col-lg-10">
                    <div class="media align-items-center mb-2">
                      <div class="media-body">
                        <h4 class="card-title mb-0 text-white">{{ $t(feature.title) }}</h4>
                      </div>
                    </div>
                    <p class="quick-link-description text-white">{{ $t(feature.description) }}</p>
                    <a
                      v-if="feature.button && feature.button.text"
                      class="theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline theme-gallery-button"
                      :href="feature.button.url"
                      target="_blank"
                    >{{ feature.button.text }}</a>
                  </div>
                </div>
              </div>
              <!-- End Feature Card -->
            </div>

          </div>
        </div>
        <!-- ========== End Feature Section ========== -->

        <!-- ========== How it Works Section ========== -->
        <div
          v-if="providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hiw_section && providerInfo[0].theme_data.landing.hiw_section.items && providerInfo[0].theme_data.landing.hiw_section.items.length > 0"
          :class="providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hiw_section && providerInfo[0].theme_data.landing.hiw_section.title ? 'space-1' : ''"
        >
          <div class="container">
            <template v-if="providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.hiw_section && providerInfo[0].theme_data.landing.hiw_section.title">
              <div class="d-flex justify-content-start mb-5 mb-lg-0 w-100">
                <h2 class="col-12 text-white text-left">
                  {{ providerInfo[0].theme_data.landing.hiw_section.title }}
                </h2>
              </div>
              <div class="container position-relative">
                <hr>
                <div class="gallery-line-background" />
              </div>
            </template>
            <div class="row justify-content-center align-items-center position-relative space-1 space-md-0 mx-1">
              <!-- How it works Card -->
              <div
                v-if="currentWindowType === 'lg'"
                class="col-12 col-lg-6 w-80 mx-auto mx-sm-auto mx-md-auto mb-5 mt-4 gallery-hiw-image-container"
              >
                <img
                  v-if="currentHiwCardIndex !== null && providerInfo[0].theme_data.landing.hiw_section.items[currentHiwCardIndex].image_url"
                  v-img="{ type:'xl', class: 'gallery-hiw-image' }"
                  class="default-img w-80 h-80 w-lg-90 h-lg-85 mr-auto"
                  :src="providerInfo[0].theme_data.landing.hiw_section.items[currentHiwCardIndex].image_url"
                >
              </div>
              <div class="col-12 col-lg-6">
                <div
                  v-for="(item, hiwIndex) in providerInfo[0].theme_data.landing.hiw_section.items"
                  :key="'hiw-' + hiwIndex"
                  :class="hiwIndex !== currentHiwCardIndex ? 'dz-dropzone' : ''"
                  @click="changeHiwCardIndex(hiwIndex)"
                >
                  <div class="mb-3">
                    <div :class="`h-100 gallery-card ${hiwIndex !== currentHiwCardIndex ? 'unactive-gallery-card' : 'card'}`">
                      <div :class="`card-body gallery-card-body pr-lg-4 pl-lg-4 d-flex flex-column ${hiwIndex === currentHiwCardIndex ? 'pt-4 pb-2' : ''}`">
                        <div class="media align-items-center">
                          <div :class="`media-body justify-content-between d-flex ${hiwIndex === currentHiwCardIndex ? 'mb-3' : ''}`">
                            <h3 class="card-title text-white mb-0">{{ $t(item.title) }}</h3>
                            <div class="text-white">
                              <i :class="`far ${hiwIndex === currentHiwCardIndex ? 'fa-angle-up' : 'fa-angle-down'}`" />
                            </div>
                          </div>
                        </div>
                        <p
                          v-if="hiwIndex === currentHiwCardIndex"
                          class="how-it-works-description text-white"
                        >{{ $t(item.description) }}</p>
                      </div>
                      <div
                        v-if="hiwIndex === currentHiwCardIndex"
                        class="gallery-card-footer font-weight-bold"
                      >
                        <a
                          v-if="item.button && item.button.text"
                          :key="item.button.text"
                          class="theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline theme-gallery-button"
                          :href="item.button.url"
                          target="_blank"
                        >{{ item.button.text }}</a>
                      </div>
                      <div v-if="currentWindowType !== 'lg' && hiwIndex === currentHiwCardIndex" class="mb-4 mx-4 gallery-hiw-image-container">
                        <img
                          v-if="currentHiwCardIndex !== null && providerInfo[0].theme_data.landing.hiw_section.items[currentHiwCardIndex].image_url"
                          v-img="{ type:'xl', class: 'gallery-hiw-small-image' }"
                          class="default-img w-80 h-80 w-lg-90 h-lg-85 mx-auto"
                          :src="providerInfo[0].theme_data.landing.hiw_section.items[currentHiwCardIndex].image_url"
                        >
                      </div>
                    </div>
                  </div>
                  <hr v-if="hiwIndex !== currentHiwCardIndex">
                </div>
              </div>
            </div>
            <!-- End How it works Card -->
          </div>
        </div>
        <!-- ========== End How it Works Section ========== -->

        <!-- ========== Call to Action Section ========== -->
        <div v-if="providerInfo[0] && providerInfo[0].theme_data && providerInfo[0].theme_data.landing && providerInfo[0].theme_data.landing.call_to_action_section && providerInfo[0].theme_data.landing.call_to_action_section.title" class="section container gallery-card space-1 space-lg-2 mt-5 mb-5 mt-md-9 mb-md-9 gallery-call-to-action-background w-100">
          <div class="container d-md-flex">
            <div class="col-12 col-md-7 d-flex align-items-md-center pl-lg-5 pr-lg-5">
              <h2 class="title text-white">{{ providerInfo[0].theme_data.landing.call_to_action_section.title }}</h2>
            </div>
            <div class="col-12 col-md-5">
              <p class="description mb-0 text-white">{{ providerInfo[0].theme_data.landing.call_to_action_section.description }}</p>
              <div v-if="providerInfo[0].theme_data.landing.call_to_action_section.buttons && providerInfo[0].theme_data.landing.call_to_action_section.buttons.length > 0" class="button-container flex-column flex-lg-row mt-3">
                <a
                  v-for="(button, index) in providerInfo[0].theme_data.landing.call_to_action_section.buttons"
                  :key="button.text"
                  :class="`${index % 2 === 0 ? 'theme-button btn btn-primary mr-2 mb-2 mr-lg-2 text-white' : 'theme-button-outline btn btn-outline-primary btn-white mb-2 theme-gallery-button-outline'} theme-gallery-button`"
                  :href="button.url"
                  target="_blank"
                >{{ button.text }}</a>
              </div>
            </div>
          </div>
        </div>
        <!-- ========== End Call to Action Section ========== -->
      </div>
    </div>

    <div id="stickyBlockEndPoint" />
    <a
      class="js-go-to go-to position-fixed invisible"
      href="#stickyBlockStartPoint"
      data-hs-go-to-options="{
          &quot;targetSelector&quot; : &quot;#logoAndNav&quot;,
          &quot;offsetTop&quot;: 200,
          &quot;position&quot;: {
            &quot;init&quot;: {
              &quot;right&quot;: 30
            },
            &quot;show&quot;: {
              &quot;bottom&quot;: 30
            },
            &quot;hide&quot;: {
              &quot;bottom&quot;: -30
            }
          }
        }"
    >
      <i class="fas fa-angle-up" />
    </a>
    <!-- ========== End Go to Top ========== -->
  </div>
</template>

<script>
const $ = require('jquery')
import ApiCarousel from '@/components/api-carousel'
import HSGoTo from '@/assets/vendor/hs-go-to/src/js/hs-go-to'
import providerInfoMixin from '@/mixins/provider-info'
import QuickLinks from './data/quick-links.json'
import { mapGetters } from 'vuex'
import Slick from 'vue-slick'

export default {
  name: 'ProviderInfoHomeGallery',
  components: {
    ApiCarousel,
    Slick
  },
  mixins: [providerInfoMixin],
  props: {
    latestApis: {
      type: Array,
      default: () => []
    },
    themeColor: {
      type: String,
      default: '#57ABF0'
    }
  },
  data() {
    return {
      home_cards: QuickLinks,
      loaded: true,
      currentHiwCardIndex: 0,
      slickOptions: {
        'infinite': true,
        'arrows': false,
        'autoplay': true,
        'autoplaySpeed': 4000,
        'slidesToShow': 3,
        'centerMode': true,
        'centerPadding': '200px',
        'dots': true,
        'dotsClass': 'theme-carousel-dots',
        'responsive': [
          {
            'breakpoint': 1400,
            'settings': {
              'slidesToShow': 2,
              'centerPadding': '180px'
            }
          },
          {
            'breakpoint': 992,
            'settings': {
              'slidesToShow': 2,
              'centerPadding': '120px'
            }
          },
          {
            'breakpoint': 768,
            'settings': {
              'slidesToShow': 1,
              'centerPadding': '80px'
            }
          },
          {
            'breakpoint': 554,
            'settings': {
              'slidesToShow': 1,
              'centerPadding': '50px'
            }
          }
        ]
      },
      currentWindowType: null
    }
  },
  computed: {
    ...mapGetters(['userProfile', 'loading'])
  },
  watch: {
    async userProfile() {
      await this.initialise()
    },
    latestApis: {
      deep: true,
      handler() {}
    }
  },
  mounted() {
    new HSGoTo($('.js-go-to')).init()

    window.addEventListener('resize', () => {
      const width = window.innerWidth
      if (width < 992 && this.currentWindowType === 'lg') {
        this.currentWindowType = 'md'
        return
      }
      if (width >= 992 && this.currentWindowType === 'md') {
        this.currentWindowType = 'lg'
        return
      }
    })
  },
  async created() {
    await this.initialise()
  },
  methods: {
    async initialise() {
      this.$store.dispatch('app/setLoading', true)
      this.loaded = false
      const width = window.innerWidth
      if (width < 992) {
        this.currentWindowType = 'md'
      }
      if (width >= 992) {
        this.currentWindowType = 'lg'
      }
      var routeParams = this.$route.params && this.$route.params.provider_name.split('-')
      this.provider_short_id = routeParams[routeParams.length - 1]
      this.loaded = true
      this.$store.dispatch('app/setLoading', false)
    },
    goToApiInfo(item) {
      try {
        this.$router.push({
          name: 'api-info-branded',
          params: {
            provider_name: (item.display_name.toLowerCase().replace(/[^A-Za-z0-9]/g, '-').concat(`-${item.provider_short_id}`)).replace(/\-+/g, '-'),
            api_name: (item.api_name.toLowerCase().replace(/[^A-Za-z0-9]/g, '-').concat(`-${item.api_short_id}`)).replace(/\-+/g, '-'),
            tab_name: 'readme'
          }
        })
      } catch (err) {
        console.log('err in goToApiInfo', err)
      }
    },
    changeHiwCardIndex(cardIndex) {
      this.currentHiwCardIndex = cardIndex
    }
  }
}

</script>

<style lang="scss" scoped>
  @import '~slick-carousel/slick/slick.css';
  @import '~slick-carousel/slick/slick-theme.css';
  // @import '../../../../assets/vendor/slick-carousel/slick/slick-theme.scss';

  .whitespace {
    white-space: pre-wrap;
  }

  .section-container {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  .section {
    // width: 80%;
    display: flex;
    justify-content: center;
    margin: auto;
    flex-direction: column;
    padding-left: 0px;
    padding-right: 0px;
  }

  .grouped-section {
    @extend .section;
    width: 95%;
  }

  .gallery-hero-section-container {
    width: 100%;
    margin: auto;
    .gallery-hero-section {
      .gallery-hero-description-section {
        // max-width: 1200px;
        .title {
          margin-bottom: 8px;
          color: #ffffff;
        }
        .description {
          font-size: 19px;
          margin-bottom: 20px;
          color: #ffffff !important;
        }
      }

      .gallery-hero-cover-image-container {
        border-radius: 0.3125rem !important;
        padding-right: 0px !important;
        overflow: hidden;
        position: relative;
        .gallery-branded-cover-image {
          background-size: cover;
          border-radius: 0.3125rem !important;
          position: absolute;
        }
      }
    }
  }

  .gallery-card-icon {
    font-size: 3rem;
  }

  .gallery-line-background {
    position: absolute;
    background: #fff;
    height: 5px;
    width: 93px;
    top: calc(50% - 2.5px);
  }

  .project-highlights-section {
    text-align: center;
    .title {
      text-align: center;
    }

    .statistic-container {
      .statistic {
        flex: 1 1 100%;
        .number {
          font-size: 40px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .subtitle {
          font-size: 20px;
        }
      }
    }

    .statistic-container:last-child {
      margin-bottom: 0 !important;
    }

    .hr {
      border-right: 0.5px solid #E5E5E5;
    }
  }

  .gallery-carousel-images-container {
    padding-left: 8px;
    padding-right: 8px;
    aspect-ratio: 3/2;
  }

  .gallery-call-to-action-background {
    background: url('/assets/img/pattern-1.svg');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: top center;
  }

  .gallery-hiw-image-container {
    // aspect-ratio: 4/3;
    display: flex;
    align-items: center;
  }

  .invisible {
    visibility: hidden;
  }
</style>
